import { useSyncExternalStore } from "react";
import type {
  CmmdCampaign,
  CmmdProject,
  CmmdTask,
  CmmdUserIdentity,
  CmmdWorkIntake,
  CmmdWorkspace,
} from "@cmmd-forge/contracts";
import { removeProjectFromCachedCmmdWorkIntake } from "../cmmdWorkIntakeCache";

export interface CmmdWorkIntakeSnapshot {
  readonly connected: boolean;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly source: string | null;
  readonly fetchedAt: string | null;
  readonly workspace: CmmdWorkspace | null;
  readonly identity: CmmdUserIdentity | null;
  readonly campaigns: readonly CmmdCampaign[];
  readonly projects: readonly CmmdProject[];
  readonly tasks: readonly CmmdTask[];
  readonly workspaces: readonly CmmdWorkspace[];
  readonly refetchRequestedAt: number | null;
  readonly authoritativeRefetchRequested?: boolean;
}

const INITIAL_WORK_INTAKE_SNAPSHOT: CmmdWorkIntakeSnapshot = {
  connected: false,
  isLoading: false,
  error: null,
  source: null,
  fetchedAt: null,
  workspace: null,
  identity: null,
  campaigns: [],
  projects: [],
  tasks: [],
  workspaces: [],
  refetchRequestedAt: null,
  authoritativeRefetchRequested: false,
};

type Listener = () => void;

let snapshot = INITIAL_WORK_INTAKE_SNAPSHOT;
let dataVersion = 0;
const listeners = new Set<Listener>();

function emit(next: CmmdWorkIntakeSnapshot): void {
  snapshot = next;
  for (const listener of listeners) listener();
}

function update(updater: (current: CmmdWorkIntakeSnapshot) => CmmdWorkIntakeSnapshot): void {
  emit(updater(snapshot));
}

function emitIntake(intake: CmmdWorkIntake): void {
  emit({
    connected: intake.connected,
    isLoading: false,
    error: null,
    source: intake.source,
    fetchedAt: intake.fetchedAt,
    workspace: intake.workspace,
    identity: intake.identity,
    campaigns: intake.campaigns,
    projects: intake.projects,
    tasks: intake.tasks,
    workspaces: intake.workspaces,
    refetchRequestedAt: snapshot.refetchRequestedAt,
    authoritativeRefetchRequested: snapshot.authoritativeRefetchRequested ?? false,
  });
}

export function subscribeCmmdWorkIntake(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function readCmmdWorkIntakeSnapshot(): CmmdWorkIntakeSnapshot {
  return snapshot;
}

export function useCmmdWorkIntakeSnapshot(): CmmdWorkIntakeSnapshot {
  return useSyncExternalStore(
    subscribeCmmdWorkIntake,
    readCmmdWorkIntakeSnapshot,
    readCmmdWorkIntakeSnapshot,
  );
}

export const cmmdWorkIntakeResource = {
  clear(): void {
    dataVersion += 1;
    emit(INITIAL_WORK_INTAKE_SNAPSHOT);
  },

  beginDataRequest(): number {
    // Every request gets a distinct version so a slower, older response cannot
    // overwrite a newer authoritative picker refresh (for example after a
    // project rename). Permanent removals also advance this same fence.
    dataVersion += 1;
    return dataVersion;
  },

  isDataVersionCurrent(expectedVersion: number): boolean {
    return dataVersion === expectedVersion;
  },

  requestRefetch(options: { readonly authoritative?: boolean } = {}): void {
    update((current) => ({
      ...current,
      // Same-millisecond picker opens must still publish distinct requests.
      refetchRequestedAt: Math.max(Date.now(), (current.refetchRequestedAt ?? 0) + 1),
      authoritativeRefetchRequested: options.authoritative === true,
    }));
  },

  setBackgroundRefreshError(message: string): void {
    update((current) => ({ ...current, error: message, isLoading: false }));
  },

  setData(intake: CmmdWorkIntake): void {
    emitIntake(intake);
  },

  setDataIfCurrent(intake: CmmdWorkIntake, expectedVersion: number): boolean {
    if (dataVersion !== expectedVersion) return false;
    emitIntake(intake);
    return true;
  },

  setError(message: string): void {
    emit({
      ...INITIAL_WORK_INTAKE_SNAPSHOT,
      error: message,
    });
  },

  setLoading(loading: boolean): void {
    update((current) => ({ ...current, isLoading: loading, error: null }));
  },

  patchTask(
    taskId: string,
    patch: Partial<
      Pick<
        CmmdTask,
        "title" | "description" | "status" | "priority" | "dueDate" | "assigneeId" | "updatedAt"
      >
    >,
  ): void {
    update((current) => ({
      ...current,
      tasks: current.tasks.map((task) => (task.id === taskId ? { ...task, ...patch } : task)),
    }));
  },

  patchTasks(
    taskIds: readonly string[],
    patch: Partial<
      Pick<
        CmmdTask,
        "title" | "description" | "status" | "priority" | "dueDate" | "assigneeId" | "updatedAt"
      >
    >,
  ): void {
    if (taskIds.length === 0) return;
    const ids = new Set(taskIds);
    update((current) => ({
      ...current,
      tasks: current.tasks.map((task) => (ids.has(task.id) ? { ...task, ...patch } : task)),
    }));
  },

  replaceTasks(tasks: readonly CmmdTask[]): void {
    update((current) => ({ ...current, tasks }));
  },

  patchProject(
    projectId: string,
    patch: Partial<Pick<CmmdProject, "description" | "brief" | "status">>,
  ): void {
    update((current) => ({
      ...current,
      projects: current.projects.map((project) =>
        project.id === projectId ? { ...project, ...patch } : project,
      ),
    }));
  },

  removeProject(projectId: string): void {
    // Invalidate every read that began before the authoritative deletion. A
    // startup, poll, or realtime response may still complete afterward; its
    // captured version prevents it from restoring this project or its cache.
    dataVersion += 1;
    removeProjectFromCachedCmmdWorkIntake(projectId);
    update((current) => ({
      ...current,
      projects: current.projects.filter((project) => project.id !== projectId),
      tasks: current.tasks.filter((task) => task.projectId !== projectId),
    }));
  },

  applyExternalProjectRemovals(projectIds: readonly string[]): void {
    // A different same-origin tab published the durable eviction fence. Do not
    // write it again; just invalidate this tab's in-flight reads and visible
    // snapshot before requesting an authoritative refresh.
    dataVersion += 1;
    const removedProjectIds = new Set(projectIds);
    update((current) => ({
      ...current,
      projects: current.projects.filter((project) => !removedProjectIds.has(project.id)),
      tasks: current.tasks.filter(
        (task) => task.projectId === null || !removedProjectIds.has(task.projectId),
      ),
    }));
  },
};
